import pick from 'lodash/pick';
import omit from 'lodash/omit';

import { fetchPaypalMerchantSignupStatus } from '../../ducks/paypal.duck';
import { fetchCurrentUser, currentUserShowSuccess } from '../../ducks/user.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { parse, stringify } from '../../util/urlHelpers';
import * as log from '../../util/log';

export const SET_INITIAL_VALUES = 'app/PayPalMerchantPage/SET_INITIAL_VALUES';
export const SAVE_MERCHANT_REQUEST = 'app/PayPalMerchantPage/SAVE_MERCHANT_REQUEST';
export const SAVE_MERCHANT_SUCCESS = 'app/PayPalMerchantPage/SAVE_MERCHANT_SUCCESS';
export const SAVE_MERCHANT_ERROR = 'app/PayPalMerchantPage/SAVE_MERCHANT_ERROR';
export const ONBOARDING_RETURN_REQUEST = 'app/PayPalMerchantPage/ONBOARDING_RETURN_REQUEST';
export const ONBOARDING_RETURN_SUCCESS = 'app/PayPalMerchantPage/ONBOARDING_RETURN_SUCCESS';
export const ONBOARDING_RETURN_ERROR = 'app/PayPalMerchantPage/ONBOARDING_RETURN_ERROR';

const initialState = {
  merchantSaveInProgress: false,
  merchantSaved: false,
  merchantSaveError: null,
  onboardingReturn: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case ONBOARDING_RETURN_REQUEST:
      return {
        ...state,
        onboardingReturn: {
          key: payload,
          inProgress: true,
          complete: false,
          error: null,
        },
      };
    case ONBOARDING_RETURN_SUCCESS:
      return {
        ...state,
        onboardingReturn: {
          key: payload,
          inProgress: false,
          complete: true,
          error: null,
        },
      };
    case ONBOARDING_RETURN_ERROR:
      return {
        ...state,
        onboardingReturn: { ...payload, inProgress: false, complete: false },
      };
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };
    case SAVE_MERCHANT_REQUEST:
      return {
        ...state,
        merchantSaveInProgress: true,
        merchantSaveError: null,
      };
    case SAVE_MERCHANT_SUCCESS:
      return {
        ...state,
        merchantSaveInProgress: false,
        merchantSaved: true,
        merchantSaveError: null,
      };
    case SAVE_MERCHANT_ERROR:
      return {
        ...state,
        merchantSaveInProgress: false,
        merchantSaved: false,
        merchantSaveError: payload,
      };
    default:
      return state;
  }
}

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const saveMerchantRequest = () => ({ type: SAVE_MERCHANT_REQUEST });
const saveMerchantSuccess = () => ({ type: SAVE_MERCHANT_SUCCESS });
const saveMerchantError = error => ({
  type: SAVE_MERCHANT_ERROR,
  payload: error,
  error: true,
});

const updatePaypalMerchantProfile = publicData => (dispatch, getState, sdk) => {
  dispatch(saveMerchantRequest());
  return sdk.currentUser
    .updateProfile({ publicData }, { expand: true })
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }

      const currentUser = entities[0];
      dispatch(currentUserShowSuccess(currentUser));
      dispatch(saveMerchantSuccess());
      return currentUser;
    })
    .catch(e => {
      log.error(e, 'save-paypal-merchant-profile-failed');
      const error = storableError(e);
      dispatch(saveMerchantError(error));
      throw error;
    });
};

const isTrue = value => value === true || value === 'true';

const PAYPAL_CALLBACK_PARAMS = [
  'merchantId',
  'merchantIdInPayPal',
  'productIntentId',
  'isEmailConfirmed',
  'accountStatus',
  'permissionsGranted',
  'consentStatus',
  'riskStatus',
];

export const isPayPalOnboardingReturn = queryParams =>
  ['merchantId', 'merchantIdInPayPal'].some(key =>
    Object.prototype.hasOwnProperty.call(queryParams || {}, key)
  );

export const getPayPalOnboardingReturnKey = queryParams =>
  isPayPalOnboardingReturn(queryParams)
    ? JSON.stringify(pick(queryParams, PAYPAL_CALLBACK_PARAMS))
    : null;

export const removePayPalCallbackParams = search => {
  const remaining = stringify(omit(parse(search), PAYPAL_CALLBACK_PARAMS));
  return remaining ? `?${remaining}` : '';
};

export const isSuccessfulPayPalOnboardingReturn = queryParams => {
  const { merchantId: trackingId, merchantIdInPayPal, permissionsGranted, consentStatus } =
    queryParams || {};

  return Boolean(
    typeof trackingId === 'string' &&
      trackingId &&
      typeof merchantIdInPayPal === 'string' &&
      merchantIdInPayPal &&
      isTrue(permissionsGranted) &&
      isTrue(consentStatus)
  );
};

export const getPayPalOnboardingProfileData = queryParams => {
  if (!isSuccessfulPayPalOnboardingReturn(queryParams)) {
    return null;
  }

  const {
    merchantId: trackingId,
    merchantIdInPayPal,
    permissionsGranted,
    consentStatus,
    accountStatus,
    isEmailConfirmed,
    productIntentId,
    riskStatus,
  } = queryParams;

  return {
    merchantId: merchantIdInPayPal,
    isPaypalConnected: false,
    paypalOnboarding: {
      trackingId,
      merchantIdInPayPal,
      permissionsGranted: isTrue(permissionsGranted),
      consentStatus: isTrue(consentStatus),
      ...(accountStatus ? { accountStatus } : {}),
      ...(isEmailConfirmed !== undefined ? { isEmailConfirmed: isTrue(isEmailConfirmed) } : {}),
      ...(productIntentId ? { productIntentId } : {}),
      ...(riskStatus ? { riskStatus } : {}),
    },
  };
};

// Share pending work within a Redux store, without sharing accounts between SSR requests.
const pendingOnboardingReturns = new WeakMap();

const refreshCurrentUser = async (dispatch, getState) => {
  await dispatch(fetchCurrentUser());
  // fetchCurrentUser records failures in Redux instead of rejecting its promise.
  const { currentUser, currentUserShowError } = getState().user;
  if (currentUserShowError || !currentUser?.id) {
    throw currentUserShowError || new Error('Could not load the current PayPal seller.');
  }
  return currentUser;
};

export const processPayPalOnboardingReturn = queryParams => (dispatch, getState) => {
  const key = getPayPalOnboardingReturnKey(queryParams);
  if (!key) return Promise.resolve(null);

  const pending = pendingOnboardingReturns.get(getState);
  if (pending) {
    return pending.key === key
      ? pending.promise
      : pending.promise
          .catch(() => null)
          .then(() => dispatch(processPayPalOnboardingReturn(queryParams)));
  }

  dispatch({ type: ONBOARDING_RETURN_REQUEST, payload: key });
  const promise = (async () => {
    try {
      const profileData = getPayPalOnboardingProfileData(queryParams);
      if (!profileData)
        throw new Error('PayPal onboarding did not grant the required permissions.');

      const currentUser = await refreshCurrentUser(dispatch, getState);
      if (currentUser.id.uuid !== profileData.paypalOnboarding.trackingId) {
        throw new Error('The PayPal onboarding return belongs to a different marketplace user.');
      }

      const publicData = currentUser.attributes.profile.publicData || {};
      const sameMerchant = publicData.merchantId === profileData.merchantId;
      const alreadyConnected = sameMerchant && publicData.isPaypalConnected === true;
      const callbackSaved =
        sameMerchant &&
        Object.entries(profileData.paypalOnboarding).every(
          ([field, value]) => publicData.paypalOnboarding?.[field] === value
        );

      if (!callbackSaved) {
        await dispatch(
          updatePaypalMerchantProfile({
            ...profileData,
            // Replaying a callback must not reset an already verified connection.
            isPaypalConnected: alreadyConnected,
          })
        );
      }

      if (!alreadyConnected) {
        // This existing endpoint verifies seller ownership/readiness with PayPal
        // and persists isPaypalConnected. Redirect flags alone cannot establish it.
        await dispatch(fetchPaypalMerchantSignupStatus());
      }
      const refreshedUser = await refreshCurrentUser(dispatch, getState);
      const persisted = refreshedUser.attributes.profile.publicData;
      if (
        persisted?.merchantId !== profileData.merchantId ||
        persisted?.isPaypalConnected !== true
      ) {
        throw new Error('PayPal has not confirmed that this account is ready to receive payments.');
      }

      dispatch({ type: ONBOARDING_RETURN_SUCCESS, payload: key });
      return refreshedUser;
    } catch (error) {
      log.error(error, 'paypal-onboarding-return-failed');
      const storedError = storableError(error);
      dispatch({
        type: ONBOARDING_RETURN_ERROR,
        payload: { key, error: storedError },
      });
      throw storedError;
    }
  })().finally(() => pendingOnboardingReturns.delete(getState));
  pendingOnboardingReturns.set(getState, { key, promise });
  return promise;
};

export const disconnectPayPalMerchant = () =>
  updatePaypalMerchantProfile({
    merchantId: null,
    isPaypalConnected: false,
    paypalOnboarding: null,
  });

export const loadData = (params, search) => async (dispatch, getState) => {
  const queryParams = parse(search);
  const isCallback = isPayPalOnboardingReturn(queryParams);
  if (isCallback) {
    await dispatch(processPayPalOnboardingReturn(queryParams));
  } else {
    dispatch(setInitialValues());
    await refreshCurrentUser(dispatch, getState);
  }

  const merchantId = getState()?.user?.currentUser?.attributes?.profile?.publicData?.merchantId;
  const status = getState().paypal.paypalMerchant;
  if (merchantId && (!isCallback || status?.merchantId !== merchantId)) {
    await dispatch(fetchPaypalMerchantSignupStatus());
    // The backend derives the connected flag from PayPal's verified seller status.
    await refreshCurrentUser(dispatch, getState);
  }

  return null;
};
